import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from "react-router-dom";
import api from '../../api/axiosInstance';
import { useModal } from '../../contexts/ModalContext';

function convertLocalToUTC(localDateTime) {
  // Create a Date object from the local date-time string.
  const date = new Date(localDateTime);
  // Get the UTC components.
  const utcYear = date.getUTCFullYear();
  const utcMonth = String(date.getUTCMonth() + 1).padStart(2, '0');
  const utcDay = String(date.getUTCDate()).padStart(2, '0');
  const utcHours = String(date.getUTCHours()).padStart(2, '0');
  const utcMinutes = String(date.getUTCMinutes()).padStart(2, '0');
  console.log(`${utcYear}-${utcMonth}-${utcDay}T${utcHours}:${utcMinutes}`);
  // Return in the ISO-like format without seconds.
  return `${utcYear}-${utcMonth}-${utcDay}T${utcHours}:${utcMinutes}`;
}

// Options for filter fields and operators
const fieldOptions = [
  { value: 'createdAt', label: 'Created At' },
  { value: 'tags', label: 'Tags' },
  { value: 'status', label: 'Status' },
];

const operatorOptions = {
  createdAt: [
    { value: 'greaterThan', label: 'After' },
    { value: 'lessThan', label: 'Before' }
  ],
  tags: [
    { value: 'in', label: 'In (multi-select)' },
    // { value: 'equals', label: 'Equals' },
    // { value: 'notEquals', label: 'Not Equals' },
    { value: 'isNull', label: 'Is Null' }
  ],
  status: [
    { value: 'equals', label: 'Equals' },
    { value: 'notEquals', label: 'Not Equals' }
  ]
};

const QueryBuilder = () => {
  const navigate = useNavigate();
  const [setBreadcrumb, setBreadcrumbLink] = useOutletContext();
  const { openModal } = useModal();

  // Automation and template states
  const [proceeding, setProceeding] = useState(false);
  const [proceedingRefresh, setProceedingRefresh] = useState(false);
  const [leadCount, setLeadCount] = useState(0);
  const [automationName, setAutomationName] = useState('');
  const [templates, setTemplates] = useState(null);
  const [templateId, setTemplateId] = useState(null);
  const [error, setError] = useState('');

  // Global filter match type (all = AND, any = OR) and includeNoTags option
  const [matchType, setMatchType] = useState('all');
  const [includeNoTags, setIncludeNoTags] = useState(true);

  // Available tags for multi-select when field is "tags"
  const [availableTags, setAvailableTags] = useState([]);

  // Query builder filters state: each filter has a unique id, field, operator, and value
  // Default filter is createdAt, but this will update if the user changes the field.
  const [filters, setFilters] = useState([
    { id: Date.now(), field: 'createdAt', operator: 'greaterThan', value: '' }
  ]);

  useEffect(() => {
    setBreadcrumb(['Marketing', 'Automations', 'Create']);
    setBreadcrumbLink(['/Marketing', '/Marketing/Automations']);
  }, [setBreadcrumb, setBreadcrumbLink]);

  // Fetch templates from the backend
  const fetchTemplates = async () => {
    try {
      const response = await api.get(`/templates/list`);
      if (!response.data.error) {
        setTemplates(response.data.templates);
        setTemplateId(response.data.templates[0]._id);
      } else {
        openModal('failure', response.data.message);
      }
    } catch (err) {
      if (err.message === 'Network Error') {
        setError('No Internet Connection');
      } else {
        openModal('failure', err.message);
      }
    }
  };

  // Fetch available tags for the multi-select
  const fetchAvailableTags = async () => {
    try {
      const response = await api.get(`/automation/tags`);
      if (!response.data.error) {
        setAvailableTags(response.data.tags);
      } else {
        openModal('failure', response.data.message);
      }
    } catch (err) {
      if (err.message === 'Network Error') {
        openModal('failure', 'No Internet Connection');
      } else {
        openModal('failure', err.message);
      }
    }
  };

  const effectiveFieldOptions =
    Array.isArray(availableTags) && availableTags.length === 0
      ? fieldOptions.filter(opt => opt.value !== 'tags')
      : fieldOptions;


  useEffect(() => {
    fetchTemplates();
    fetchAvailableTags();
  }, []);

  // Update a filter in the filters array.
  // If field is changed, reset operator to default and assign default value based on field.
  const updateFilter = (id, key, value) => {
    setFilters(prev =>
      prev.map(filter => {
        if (filter.id === id) {
          const updatedFilter = { ...filter, [key]: value };
          if (key === 'field') {
            // Reset operator to the first operator for the new field.
            updatedFilter.operator = operatorOptions[value][0].value;
            // Set default value based on field.
            if (value === 'tags') {
              updatedFilter.value = []; // empty array for multi-select
            } else if (value === 'status') {
              updatedFilter.value = 'contacted'; // default status value
            } else {
              updatedFilter.value = '';
            }
          }
          return updatedFilter;
        }
        return filter;
      })
    );
  };

  // Add a new filter row (default to createdAt)
  const addFilter = () => {
    setFilters([...filters, { id: Date.now(), field: 'createdAt', operator: 'greaterThan', value: '' }]);
  };

  // Remove a filter row
  const removeFilter = (id) => {
    setFilters(filters.filter(filter => filter.id !== id));
  };

  // Map filters state to conditions expected by the backend
  const buildConditions = () => {
    return filters
      .filter(filter => {
        // For tags, ensure there is at least one selected value
        if (filter.field === 'tags') {
          return Array.isArray(filter.value) && filter.value.length > 0;
        }
        return filter.value !== '';
      })
      .map(filter => {
        let { field, operator, value } = filter;
        if (field === 'createdAt' && value) {
          value = convertLocalToUTC(value);
        }
        // For tags with operator "in", if value is an array, use it directly.
        if (field === 'tags' && operator === 'in' && Array.isArray(value)) {
          // value remains an array.
        }
        return { field, operator, value };
      });
  };

  // Validate that at least one filter has a value
  const validateFilters = () => {
    const validFilters = filters.filter(filter => {
      if (filter.field === 'tags') {
        return Array.isArray(filter.value) && filter.value.length > 0;
      }
      return filter.value !== '';
    });
    if (validFilters.length === 0) {
      openModal('failure', 'Please provide at least one filter.');
      return false;
    }
    return true;
  };

  // Fetch lead count based on filters
  const fetchLeadCount = async () => {
    if (!validateFilters()) return;
    const conditions = buildConditions();
    try {
      setProceedingRefresh(true);
      const response = await api.post(`/automation/automated-cond-lead-count`, {
        matchType,
        conditions,
        includeNoTags
      });
      if (!response.data.error) {
        setLeadCount(response.data.noOfLeads);
      } else {
        setError(response.data.message);
      }
      setProceedingRefresh(false);
    } catch (err) {
      if (err.message === 'Network Error') {
        openModal('failure', 'No Internet Connection');
      } else {
        openModal('failure', err.message);
      }
      setProceedingRefresh(false);
    }
  };

  // Handle creating the automation
  const handleCreateAutomation = async (event) => {
    event.preventDefault();
    if (!validateFilters()) return;
    if (leadCount === 0) {
      openModal('failure', 'Number of leads cannot be zero.');
      return;
    }
    setProceeding(true);
    const conditions = buildConditions();
    try {
      const response = await api.post(`/automation/automated-cond-emails`, {
        name: automationName,
        matchType,
        conditions,
        templateId,
        includeNoTags
      });
      if (!response.data.error) {
        openModal('success', response.data.message);
        navigate('/Marketing/Automations');
      } else {
        openModal('failure', response.data.message);
      }
    } catch (err) {
      if (err.message === 'Network Error') {
        setError("No Internet Connection.");
      } else {
        openModal('failure', err.message);
      }
    }
    setProceeding(false);
  };

  if (!templates || availableTags === null) return (
    <div className="flex items-center justify-center w-full h-[90vh]">
      <div role="status">
        <svg aria-hidden="true" className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
          <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
        </svg>
        <span className="sr-only">Loading...</span>
      </div>
    </div>
  );

  return (
    <div className="p-4 bg-white sm:p-6 dark:bg-gray-800">
      {/* Query Builder Section */}
      <div className="mb-4 border p-4 rounded">
        <h2 className="text-xl font-bold mb-4">Conditions Filter</h2>
        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-gray-900">Match Type</label>
          <select
            value={matchType}
            onChange={(e) => setMatchType(e.target.value)}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5"
          >
            <option value="all">All (AND)</option>
            <option value="any">Any (OR)</option>
          </select>
        </div>
        {filters.map((filter) => (
          <div key={filter.id} className="flex flex-wrap gap-4 items-end mb-4 border p-2 rounded">
            <div className="flex-1">
              <label className="block mb-1 text-sm">Field</label>
              <select
                value={filter.field}
                onChange={(e) => updateFilter(filter.id, 'field', e.target.value)}
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5"
              >
                {effectiveFieldOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block mb-1 text-sm">Operator</label>
              <select
                value={filter.operator}
                onChange={(e) => updateFilter(filter.id, 'operator', e.target.value)}
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5"
              >
                {operatorOptions[filter.field].map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block mb-1 text-sm">Value</label>
              {filter.field === 'createdAt' ? (
                <input
                  type="datetime-local"
                  value={filter.value}
                  onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5"
                />
              ) : filter.field === 'status' ? (
                <select
                  value={filter.value}
                  onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5"
                >
                  <option value="contacted">Contacted</option>
                  <option value="untouched">Untouched</option>
                </select>
              ) : filter.field === 'tags' ? (
                <select
                  multiple
                  value={filter.value}
                  onChange={(e) => {
                    const selectedOptions = Array.from(e.target.selectedOptions).map(opt => opt.value);
                    updateFilter(filter.id, 'value', selectedOptions);
                  }}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5"
                >
                  {availableTags.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  placeholder={filter.operator === 'in' ? 'Comma separated values' : 'Enter value'}
                  value={filter.value}
                  onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5"
                />
              )}
            </div>
            <div>
              {filters.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeFilter(filter.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={addFilter}
          className="flex py-2 px-5 me-2 mb-4 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-200 dark:focus:ring-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700"
        >
          <svg className='me-2 w-5' xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" fill="currentColor"><path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z" /></svg>
          <span className='pt-0.5'>Add Filter</span>
        </button>
        <hr />
        {/* Include Leads Without Tags Checkbox */}
        <label className="mt-4 flex items-center w-fit cursor-pointer">
          <input
            type="checkbox"
            checked={includeNoTags}
            onChange={() => setIncludeNoTags(!includeNoTags)}
            className="mr-2"
          />
          <span>Include leads without tags</span>
        </label>
        <div className="mt-4 flex items-center">
          <span className="me-4 font-bold">Target Leads: {leadCount}</span>
          <button
            onClick={fetchLeadCount}
            type="button"
            className="flex py-2 px-3 me-2 mb-2 text-xs font-semibold text-gray-900 focus:outline-none bg-white rounded-full border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-200 dark:focus:ring-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700"
          >
            {proceedingRefresh ? (
              <div className="inline mr-3" role="status">
                <svg
                  aria-hidden="true"
                  className="w-4 h-4 inline text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                  <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                </svg>
                <span className="sr-only">Loading...</span>
              </div>
            ) : (
              <svg className="w-4 h-4 inline me-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" fill="#008000"><path d="M268-240 42-466l57-56 170 170 56 56-57 56Zm226 0L268-466l56-57 170 170 368-368 56 57-424 424Zm0-226-57-56 198-198 57 56-198 198Z" /></svg>
            )} Verify / Refresh
          </button>
        </div>
      </div>
      {/* Automation Creation Form */}
      <form onSubmit={handleCreateAutomation}>
        <div className="grid grid-cols-6 gap-6">
          <div className="col-span-6 sm:col-span-3">
            <label htmlFor="automationName" className="block mb-2 text-sm font-medium text-gray-900">Automation Name</label>
            <input
              type="text"
              id="automationName"
              onChange={(e) => setAutomationName(e.target.value)}
              value={automationName}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5"
              required
            />
          </div>
          <div className="col-span-6 sm:col-span-3">
            <label htmlFor="template" className="block mb-2 text-sm font-medium text-gray-900">Select Template</label>
            <select
              onChange={(e) => setTemplateId(e.target.value)}
              value={templateId}
              id="template"
              className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg block w-full p-2.5"
              required
            >
              {templates.map((template) => (
                <option key={template._id} value={template._id}>
                  {template.templateName}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-6">
            <button
              className="w-full text-white bg-green-500 hover:bg-green-600 focus:ring-4 focus:ring-gray-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
              type="submit"
              disabled={proceeding}
            >
              {proceeding ? (
                <div className="inline mr-3" role="status">
                  <svg
                    aria-hidden="true"
                    className="w-5 h-5 inline text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                    <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                  </svg>
                  <span className="sr-only">Loading...</span>
                </div>
              ) : 'Create Automation'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default QueryBuilder;
